import { useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MHButton from "../Common/Button/MHButton";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export interface PastEvent {
  date: string;
  title: string;
  body: string;
  imageUrl: string;
  onHighlightsClick?: () => void;
}

interface PastEventsCarouselProps {
  events: PastEvent[];
}

export default function PastEventsCarousel({
  events,
}: PastEventsCarouselProps) {
  const [current, setCurrent] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const GAP = isMobile ? 12 : 40;

  // On tablet only 1 card is visible (80% wide), on desktop 2 cards are visible
  const maxIndex =
    isMobile || isTablet ? events.length - 1 : Math.max(0, events.length - 2);

  const goTo = (index: number) => {
    if (index < 0 || index > maxIndex) return;
    setCurrent(index);
    if (trackRef.current) {
      const card = trackRef.current.querySelector<HTMLElement>(".event-card");
      if (card) {
        const offset = (card.offsetWidth + GAP) * index;
        trackRef.current.style.transform = `translateX(-${offset}px)`;
      }
    }
  };

  // For dots: on desktop, number of "pages" = events.length - 1 (sliding one at a time)
  // but we clamp navigation at maxIndex, so dots = maxIndex + 1
  // const dotCount = maxIndex + 1;

  const arrowSx = {
    width: 48,
    height: 48,
    backgroundColor: "rgba(20,20,20,0.6)",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    flexShrink: 0,
    "&:hover": { backgroundColor: "rgba(20,20,20,0.85)" },
    "&.Mui-disabled": { opacity: 0.2, color: "#fff" },
  };

  return (
    <Box
      sx={{ width: "100%", py: 3 }}
      className={`${events.length === 0 ? "hidden" : "block"}`}
    >
      {isMobile ? (
        <Box sx={{ position: "relative" }}>
          <Box sx={{ overflow: "hidden", px: 2 }}>
            <Box
              ref={trackRef}
              sx={{
                display: "flex",
                gap: `${GAP}px`,
                transition: "transform 0.45s cubic-bezier(0.4,0,0.2,1)",
              }}
            >
              {events.map((event, i) => (
                <Box
                  key={i}
                  className="event-card"
                  sx={{
                    position: "relative",
                    minWidth: "calc(100% - 32px)",
                    width: "calc(100% - 32px)",
                    height: 380,
                    borderRadius: "16px",
                    overflow: "hidden",
                    flexShrink: 0,
                    cursor: "pointer",
                    "&:hover .card-bg": { transform: "scale(1.04)" },
                  }}
                >
                  <Box
                    className="card-bg"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url(${event.imageUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.5s ease",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: "black",
                      opacity: 0.52,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "8%",
                      left: 0,
                      right: 0,
                      display: "flex",
                      flexDirection: "column",
                      pl: 3,
                      pr: 3,
                      gap: 1.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontSize="11px"
                      letterSpacing="0.1em"
                      color="white"
                      sx={{ textTransform: "uppercase", fontWeight: 600 }}
                    >
                      {event.date}
                    </Typography>
                    <Typography
                      variant="h2"
                      fontSize="30px"
                      lineHeight="110%"
                      letterSpacing="-0.05em"
                      color="white"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {event.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontSize="13px"
                      lineHeight="180%"
                      letterSpacing="-0.02em"
                      color="rgba(255,255,255,0.85)"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {event.body}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <MHButton
                        onClick={event.onHighlightsClick}
                        sx={{
                          color: "primary.main",
                          backgroundColor: "#fff",
                          borderRadius: "36px",
                          "&:hover": {
                            backgroundColor: "#fff",
                            color: "primary.main",
                          },
                        }}
                        className="w-fit h-8 font-areaExt text-[10px]"
                      >
                        See Event Highlights
                      </MHButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {current < maxIndex && (
            <IconButton
              onClick={() => goTo(current + 1)}
              sx={{
                ...arrowSx,
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}

          {current > 0 && (
            <IconButton
              onClick={() => goTo(current - 1)}
              sx={{
                ...arrowSx,
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => goTo(current - 1)}
            disabled={current === 0}
            sx={arrowSx}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <Box
              ref={trackRef}
              sx={{
                display: "flex",
                gap: `${GAP}px`,
                transition: "transform 0.45s cubic-bezier(0.4,0,0.2,1)",
              }}
            >
              {events.map((event, i) => (
                <Box
                  key={i}
                  className="event-card"
                  sx={{
                    position: "relative",
                    minWidth: {
                      md: "calc(50% - 20px)",
                      sm: "calc(80% - 20px)",
                    },
                    height: { md: 600, sm: 500 },
                    borderRadius: "16px",
                    overflow: "hidden",
                    flexShrink: 0,
                    cursor: "pointer",
                    "&:hover .card-bg": { transform: "scale(1.04)" },
                  }}
                >
                  <Box
                    className="card-bg"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url(${event.imageUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.5s ease",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: "black",
                      opacity: 0.55,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "10%",
                      left: 0,
                      right: 0,
                      display: "flex",
                      flexDirection: "column",
                      pl: 3,
                      pr: 3,
                      gap: 1.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontSize={{ sm: "10px", md: "12px" }}
                      lineHeight="189%"
                      letterSpacing="0.1em"
                      color="white"
                      sx={{ textTransform: "uppercase" }}
                    >
                      {event.date}
                    </Typography>
                    <Typography
                      variant="h2"
                      fontSize={{ sm: "34px", md: "40px" }}
                      lineHeight="120%"
                      letterSpacing="-0.05em"
                      color="white"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {event.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontSize="14px"
                      lineHeight="180%"
                      letterSpacing="-0.02em"
                      color="rgba(255,255,255,0.85)"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {event.body}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <MHButton
                        onClick={event.onHighlightsClick}
                        sx={{
                          color: "primary.main",
                          backgroundColor: "#fff",
                          borderRadius: "36px",
                          "&:hover": {
                            backgroundColor: "#fff",
                            color: "primary.main",
                          },
                        }}
                        className="w-fit md:w-[250px] h-8 md:h-[40px] font-areaExt text-[10px] md:text-xs"
                      >
                        See Event Highlights
                      </MHButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <IconButton
            onClick={() => goTo(current + 1)}
            disabled={current >= maxIndex} // ← KEY FIX
            sx={arrowSx}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      )}

      {/* Dots — only dotCount dots, not events.length */}
      {/* <Box
        sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 2.5 }}
      >
        {Array.from({ length: dotCount }).map((_, i) => (
          <Box
            key={i}
            onClick={() => goTo(i)}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              cursor: "pointer",
              backgroundColor:
                i === current ? "text.primary" : "action.disabled",
              transform: i === current ? "scale(1.2)" : "scale(1)",
              transition: "background-color 0.2s, transform 0.2s",
            }}
          />
        ))}
      </Box> */}
    </Box>
  );
}
